// presenceStore.js — the SINGLE source of truth for the staff presence status snapshot (WAVE 2a-3).
//
// Joey (Nov 3): the phone dock shows the SAME 4 presence states, the SAME words as the top-bar selector,
// read and written through the ONE presence record — no local-only status anywhere. (A future live-calling
// wave adds a 5th "On a call" state set automatically during a call.)
//
// The presence RECORD lives on the backend (/dispositions/presence). This module is the in-memory MIRROR of
// the caller's current status, so the top-bar PresenceSelector and the phone-dock pill — rendered in
// different parts of the tree — always reflect the same value the instant any control changes it. React 19's
// useSyncExternalStore keeps every subscriber in sync.
import { useSyncExternalStore } from 'react';
import api from '../utils/api';
import { PRESENCE_DEFAULT } from '../constants/presence';

// systemState (PP-3): a transient, system-driven overlay — 'on_call' | 'acw' | '' — set by the call
// lifecycle (callSessionStore). When present it is what the top bar + dock display, and the rep cannot
// pick a manual status; it never persists to the backend presence record. Clearing it (finish call)
// falls back to the real `status`.
let snapshot = { status: PRESENCE_DEFAULT, meetingLabel: '', systemState: '' };
const listeners = new Set();
let fetched = false;

function emit() {
  listeners.forEach((l) => { try { l(); } catch { /* ignore */ } });
}

// Update the mirror after a read or a write. Both controls call this (via commit) so they never diverge.
export function setPresenceSnapshot(status, meetingLabel) {
  const next = { status: status || PRESENCE_DEFAULT, meetingLabel: meetingLabel || '', systemState: snapshot.systemState };
  if (next.status === snapshot.status && next.meetingLabel === snapshot.meetingLabel) return;
  snapshot = next;
  emit();
}

// PP-3: set/clear the system-driven overlay ('on_call' | 'acw' | '' to clear). The call lifecycle owns
// this; both the top-bar selector and the dock read it from the one snapshot, so they never diverge.
export function setSystemPresence(systemState) {
  const next = { ...snapshot, systemState: systemState || '' };
  if (next.systemState === snapshot.systemState) return;
  snapshot = next;
  emit();
}

// Read the live status from the backend once (idempotent). Any control may call this on mount; the first
// call fetches, later calls are no-ops unless force=true. Degrades silently (keeps the Ready default).
export function fetchPresenceOnce(force = false) {
  if (fetched && !force) return;
  fetched = true;
  api.get('/dispositions/presence/current')
    .then((res) => {
      const p = res?.data?.presence;
      setPresenceSnapshot(p?.status, p?.meeting_type_label);
    })
    .catch(() => { /* leave default */ });
}

// Clear the mirror on a user switch (logout/login). The store is a module-level singleton, and this app's
// logout/login are SPA-only (no page reload), so without this a second employee signing in on the same tab
// would briefly see the previous user's status/meeting label until their first write. Call from auth.
export function resetPresenceStore() {
  fetched = false;
  snapshot = { status: PRESENCE_DEFAULT, meetingLabel: '', systemState: '' };
  emit();
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return snapshot;
}

export function usePresenceSnapshot() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
